'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { useStore } from '@base-ui/utils/store';
import { TimelineGrid } from '@mui/x-scheduler-headless-premium/timeline-grid';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-headless-premium/use-event-timeline-premium-store-context';
import { eventTimelinePremiumViewSelectors, timelineOccurrencePlaceholderSelectors } from '@mui/x-scheduler-headless-premium/event-timeline-premium-selectors';
import { schedulerNowSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { EventDialogProvider, EventDialogTrigger, useEventDialogContext } from '@mui/x-scheduler/internals';
import { DaysHeader, MonthsHeader, TimeHeader, WeeksHeader, YearsHeader } from "./view-header/index.mjs";
import EventTimelinePremiumTitleCell from "./timeline-title-cell/EventTimelinePremiumTitleCell.mjs";
import { EventTimelinePremiumEvent } from "./timeline-event/index.mjs";
import { useEventTimelinePremiumStyledContext } from "../EventTimelinePremiumStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EventTimelinePremiumContentRoot = styled('section', {
  name: 'MuiEventTimeline',
  slot: 'Content'
})(({
  theme
}) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
  flexGrow: 1,
  width: '100%',
  overflow: 'hidden'
}));
const EventTimelinePremiumGrid = styled(TimelineGrid.Root, {
  name: 'MuiEventTimeline',
  slot: 'Grid'
})({
  height: '100%',
  display: 'grid',
  gridTemplateColumns: 'fit-content(30%) minmax(0, 1fr)',
  gridTemplateRows: 'auto 1fr auto',
  alignItems: 'stretch'
});
const EventTimelinePremiumHeaderRow = styled(TimelineGrid.Row, {
  name: 'MuiEventTimeline',
  slot: 'HeaderRow'
})(({
  theme
}) => ({
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
  gridRow: 1,
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'subgrid'
}));
const EventTimelinePremiumTitleHeaderCell = styled(TimelineGrid.Cell, {
  name: 'MuiEventTimeline',
  slot: 'TitleHeaderCell'
})(({
  theme
}) => ({
  fontWeight: theme.typography.fontWeightMedium,
  padding: theme.spacing(1),
  display: 'flex',
  fontSize: theme.typography.body2.fontSize,
  alignItems: 'flex-end',
  height: '100%',
  borderRight: `1px solid ${(theme.vars || theme).palette.divider}`,
  overflowX: 'hidden'
}));
const EventTimelinePremiumEventsHeaderCell = styled(TimelineGrid.Cell, {
  name: 'MuiEventTimeline',
  slot: 'EventsHeaderCell'
})({
  position: 'relative',
  overflowX: 'clip'
});
const EventTimelinePremiumEventsHeaderCellContent = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'EventsHeaderCellContent'
})({
  overflowX: 'hidden',
  height: '100%'
});
const EventTimelinePremiumBodyScroller = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'BodyScroller'
})({
  gridColumn: '1 / -1',
  gridRow: 2,
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridTemplateRows: 'repeat(var(--row-count, 0), auto) minmax(auto, 1fr)',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin'
});
const EventTimelinePremiumTitleSubGrid = styled(TimelineGrid.SubGrid, {
  name: 'MuiEventTimeline',
  slot: 'TitleSubGrid'
})(({
  theme
}) => ({
  gridColumn: 1,
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: '1 / -1',
  borderRight: `1px solid ${(theme.vars || theme).palette.divider}`,
  overflowX: 'auto',
  overflowY: 'clip',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}));
const EventTimelinePremiumEventsSubGridWrapper = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'EventsSubGridWrapper'
})({
  overflowX: 'auto',
  overflowY: 'clip',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  gridColumn: 2,
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: '1 / -1'
});
const EventTimelinePremiumEventsSubGrid = styled(TimelineGrid.SubGrid, {
  name: 'MuiEventTimeline',
  slot: 'EventsSubGrid'
})({
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: '1 / -1',
  gridColumn: 1
});
const EventTimelinePremiumEventsSubGridRow = styled(TimelineGrid.EventRow, {
  name: 'MuiEventTimeline',
  slot: 'EventsSubGridRow'
})(({
  theme
}) => ({
  width: 'calc(var(--unit-count) * var(--unit-width))',
  minWidth: '100%',
  display: 'grid',
  gridTemplateRows: `repeat(var(--lane-count, 1), minmax(calc(${theme.typography.body2.lineHeight}em + ${theme.spacing(1)}), auto))`,
  rowGap: theme.spacing(0.5),
  position: 'relative',
  padding: theme.spacing(2, 0),
  alignContent: 'start',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const EventTimelinePremiumCurrentTimeIndicator = styled(TimelineGrid.CurrentTimeIndicator, {
  name: 'MuiEventTimeline',
  slot: 'CurrentTimeIndicator'
})(({
  theme
}) => ({
  gridRow: '1 / -1',
  gridColumn: 1,
  marginLeft: 'calc(var(--unit-count) * var(--unit-width) * var(--x-position))',
  width: 0,
  zIndex: 2,
  borderLeft: `2px solid ${theme.palette.primary.main}`,
  pointerEvents: 'none'
}));
const EventTimelinePremiumCurrentTimeIndicatorCircle = styled(TimelineGrid.CurrentTimeIndicator, {
  name: 'MuiEventTimeline',
  slot: 'CurrentTimeIndicatorCircle'
})(({
  theme
}) => ({
  position: 'absolute',
  bottom: -5,
  // 3px = half the circle's width (4px) minus half the line's width (1px), to center the circle on the line.
  left: 'calc(var(--unit-count) * var(--unit-width) * var(--x-position) - var(--events-scroll-left, 0) * 1px - 3px)',
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  zIndex: 1
}));
const EventTimelinePremiumTitleScrollbar = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'TitleScrollbar'
})(({
  theme
}) => ({
  gridRow: 3,
  gridColumn: 1,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'thin',
  borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const EventTimelinePremiumEventsScrollbar = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'EventsScrollbar'
})({
  gridRow: 3,
  gridColumn: 2,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'thin'
});
function EventRowContent({
  resourceId,
  occurrences,
  placeholder
}) {
  const store = useEventTimelinePremiumStoreContext();
  const {
    onOpen: startEditing
  } = useEventDialogContext();
  const placeholderRef = React.useRef(null);
  const isCreatingAnEvent = useStore(store, timelineOccurrencePlaceholderSelectors.isCreatingInResource, resourceId);
  React.useEffect(() => {
    if (!isCreatingAnEvent || !placeholder || !placeholderRef.current) {
      return;
    }
    startEditing(placeholderRef, placeholder);
  }, [isCreatingAnEvent, placeholder, startEditing]);
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [occurrences.map(occurrence => /*#__PURE__*/_jsx(EventDialogTrigger, {
      occurrence: occurrence,
      children: /*#__PURE__*/_jsx(EventTimelinePremiumEvent, {
        occurrence: occurrence,
        ariaLabelledBy: `TimelineTitleCell-${occurrence.resource}`,
        variant: "regular"
      })
    }, occurrence.key)), placeholder != null && /*#__PURE__*/_jsx(EventTimelinePremiumEvent, {
      ref: placeholderRef,
      occurrence: placeholder,
      ariaLabelledBy: `EventTimelinePremiumTitleCell-${placeholder.resource}`,
      variant: "placeholder"
    })]
  });
}

/**
 * Sets up bi-directional scroll sync between a content area and a scrollbar widget,
 * plus an optional header element that follows the content's scrollLeft.
 */
function useSyncedHorizontalScroll(contentRef, scrollbarRef, headerRef, onScrollLeft) {
  React.useEffect(() => {
    const content = contentRef.current;
    const scrollbar = scrollbarRef.current;
    if (!content || !scrollbar) {
      return undefined;
    }
    let syncing = false;
    const header = headerRef?.current;
    const syncScrollLeft = scrollLeft => {
      if (header) {
        header.scrollLeft = scrollLeft;
      }
      onScrollLeft?.(scrollLeft);
    };
    const handleContentScroll = () => {
      if (syncing) {
        return;
      }
      syncing = true;
      const {
        scrollLeft
      } = content;
      scrollbar.scrollLeft = scrollLeft;
      syncScrollLeft(scrollLeft);
      requestAnimationFrame(() => {
        syncing = false;
      });
    };
    const handleScrollbarScroll = () => {
      if (syncing) {
        return;
      }
      syncing = true;
      const {
        scrollLeft
      } = scrollbar;
      content.scrollLeft = scrollLeft;
      syncScrollLeft(scrollLeft);
      requestAnimationFrame(() => {
        syncing = false;
      });
    };
    syncScrollLeft(content.scrollLeft);
    content.addEventListener('scroll', handleContentScroll, {
      passive: true
    });
    scrollbar.addEventListener('scroll', handleScrollbarScroll, {
      passive: true
    });
    return () => {
      content.removeEventListener('scroll', handleContentScroll);
      scrollbar.removeEventListener('scroll', handleScrollbarScroll);
    };
  }, [contentRef, scrollbarRef, headerRef, onScrollLeft]);
}
export const EventTimelinePremiumContent = /*#__PURE__*/React.forwardRef(function EventTimelinePremiumContent(props, forwardedRef) {
  // Context hooks
  const store = useEventTimelinePremiumStoreContext();
  const {
    classes,
    localeText,
    resourceColumnLabel
  } = useEventTimelinePremiumStyledContext();

  // Ref hooks
  const containerRef = React.useRef(null);
  const eventsHeaderCellRef = React.useRef(null);
  const eventsHeaderRef = React.useRef(null);
  const eventsScrollerRef = React.useRef(null);
  const eventsScrollbarRef = React.useRef(null);
  const titleHeaderRef = React.useRef(null);
  const titleSubGridRef = React.useRef(null);
  const titleScrollbarRef = React.useRef(null);
  const titleScrollbarSpacerRef = React.useRef(null);
  const handleRef = useMergedRefs(forwardedRef, containerRef);

  // Selector hooks
  const adapter = useAdapterContext();
  const view = useStore(store, eventTimelinePremiumViewSelectors.view);
  const now = useStore(store, schedulerNowSelectors.nowUpdatedEveryMinute);
  const showCurrentTimeIndicatorSetting = useStore(store, schedulerNowSelectors.showCurrentTimeIndicator);
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);
  const isNowInView = React.useMemo(() => adapter.isWithinRange(now, [viewConfig.start, viewConfig.end]), [adapter, now, viewConfig.start, viewConfig.end]);
  const showCurrentTimeIndicator = showCurrentTimeIndicatorSetting && isNowInView;

  // Track scrollLeft as CSS variable on header cell for the current time indicator circle
  const syncCircleScroll = React.useCallback(scrollLeft => {
    eventsHeaderCellRef.current?.style.setProperty('--events-scroll-left', String(scrollLeft));
  }, []);

  // Sync horizontal scroll: events body ↔ events scrollbar + events header
  useSyncedHorizontalScroll(eventsScrollerRef, eventsScrollbarRef, eventsHeaderRef, syncCircleScroll);

  // Sync horizontal scroll: title body ↔ title scrollbar + title header
  useSyncedHorizontalScroll(titleSubGridRef, titleScrollbarRef, titleHeaderRef);

  // Keep title scrollbar spacer width in sync with title content width
  React.useEffect(() => {
    const subgrid = titleSubGridRef.current;
    const spacer = titleScrollbarSpacerRef.current;
    if (!subgrid || !spacer) {
      return undefined;
    }
    const updateWidth = () => {
      if (subgrid.scrollWidth > subgrid.clientWidth) {
        spacer.style.width = `${subgrid.scrollWidth}px`;
      } else {
        spacer.style.width = '';
      }
    };
    updateWidth();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(subgrid);
    return () => observer.disconnect();
  }, []);

  // Feature hooks
  let header;
  switch (view) {
    case 'time':
      header = /*#__PURE__*/_jsx(TimeHeader, {});
      break;
    case 'days':
      header = /*#__PURE__*/_jsx(DaysHeader, {});
      break;
    case 'weeks':
      header = /*#__PURE__*/_jsx(WeeksHeader, {});
      break;
    case 'months':
      header = /*#__PURE__*/_jsx(MonthsHeader, {});
      break;
    case 'years':
      header = /*#__PURE__*/_jsx(YearsHeader, {});
      break;
    default:
      header = null;
  }
  return /*#__PURE__*/_jsx(EventTimelinePremiumContentRoot, _extends({
    ref: handleRef,
    className: classes.content
  }, props, {
    children: /*#__PURE__*/_jsx(EventDialogProvider, {
      children: /*#__PURE__*/_jsxs(EventTimelinePremiumGrid, {
        className: classes.grid,
        style: {
          '--unit-width': `var(--${view}-cell-width)`
        },
        children: [/*#__PURE__*/_jsxs(EventTimelinePremiumHeaderRow, {
          className: classes.headerRow,
          children: [/*#__PURE__*/_jsx(EventTimelinePremiumTitleHeaderCell, {
            ref: titleHeaderRef,
            className: classes.titleHeaderCell,
            children: resourceColumnLabel ?? localeText.timelineResourceTitleHeader
          }), /*#__PURE__*/_jsxs(EventTimelinePremiumEventsHeaderCell, {
            ref: eventsHeaderCellRef,
            className: classes.eventsHeaderCell,
            children: [/*#__PURE__*/_jsx(EventTimelinePremiumEventsHeaderCellContent, {
              ref: eventsHeaderRef,
              className: classes.eventsHeaderCellContent,
              children: header
            }), showCurrentTimeIndicator && /*#__PURE__*/_jsx(EventTimelinePremiumCurrentTimeIndicatorCircle, {
              className: classes.currentTimeIndicatorCircle,
              "aria-hidden": true
            })]
          })]
        }), /*#__PURE__*/_jsxs(EventTimelinePremiumBodyScroller, {
          role: "presentation",
          children: [/*#__PURE__*/_jsx(EventTimelinePremiumTitleSubGrid, {
            ref: titleSubGridRef,
            className: classes.titleSubGrid,
            children: resourceId => /*#__PURE__*/_jsx(EventTimelinePremiumTitleCell, {
              resourceId: resourceId
            }, resourceId)
          }), /*#__PURE__*/_jsxs(EventTimelinePremiumEventsSubGridWrapper, {
            ref: eventsScrollerRef,
            role: "presentation",
            className: classes.eventsSubGridWrapper,
            children: [/*#__PURE__*/_jsx(EventTimelinePremiumEventsSubGrid, {
              className: classes.eventsSubGrid,
              children: resourceId => /*#__PURE__*/_jsx(EventTimelinePremiumEventsSubGridRow, {
                resourceId: resourceId,
                className: classes.eventsSubGridRow,
                children: ({
                  occurrences,
                  placeholder
                }) => /*#__PURE__*/_jsx(EventRowContent, {
                  resourceId: resourceId,
                  occurrences: occurrences,
                  placeholder: placeholder
                })
              }, resourceId)
            }), showCurrentTimeIndicator && /*#__PURE__*/_jsx(EventTimelinePremiumCurrentTimeIndicator, {
              className: classes.currentTimeIndicator,
              "aria-hidden": true
            })]
          })]
        }), /*#__PURE__*/_jsx(EventTimelinePremiumTitleScrollbar, {
          ref: titleScrollbarRef,
          "aria-hidden": true,
          children: /*#__PURE__*/_jsx("div", {
            ref: titleScrollbarSpacerRef,
            style: {
              height: 1
            }
          })
        }), /*#__PURE__*/_jsx(EventTimelinePremiumEventsScrollbar, {
          ref: eventsScrollbarRef,
          "aria-hidden": true,
          children: /*#__PURE__*/_jsx("div", {
            style: {
              width: 'calc(var(--unit-count) * var(--unit-width))',
              height: 1
            }
          })
        })]
      })
    })
  }));
});
if (process.env.NODE_ENV !== "production") EventTimelinePremiumContent.displayName = "EventTimelinePremiumContent";